import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { useRecoilValue, useSetRecoilState } from 'recoil';

import { getMyScribeAIPrompt } from 'api/MyScribeAi';

import { iaRa } from 'lib/helpers/utility';

import { showAlert } from 'components/shared/Alert/Alert';
import {
  ambientListeningIntelligenceChatId,
  ambientListeningMessages,
  ambientThreadHydrating
} from 'components/practice/charts/ClinicalNote/MyScribeAI/Templates/AmbientListening/state';

// across step-switch mounts, so hydrate from server only when the chat id changes.
let lastLoadedChatId = null;

export const resetHydratedThread = () => {
  lastLoadedChatId = null;
};

export default function useHydrateThread() {
  const navigate = useNavigate();
  const intelligenceChatId = useRecoilValue(ambientListeningIntelligenceChatId);
  const setMessages = useSetRecoilState(ambientListeningMessages);
  const setHydrating = useSetRecoilState(ambientThreadHydrating);

  useEffect(() => {
    if (!intelligenceChatId || intelligenceChatId === lastLoadedChatId) return undefined;
    lastLoadedChatId = intelligenceChatId;
    let cancelled = false;
    let committed = false;
    setHydrating(true);
    (async () => {
      try {
        const res = await getMyScribeAIPrompt(navigate, { id: intelligenceChatId });
        if (res?.code !== 0) throw res;
        if (!cancelled) {
          setMessages(iaRa(res?.chat));
          committed = true;
        }
      } catch (error) {
        if (!cancelled) {
          lastLoadedChatId = null;
          showAlert({
            message: "Couldn't load the conversation. Please refresh the page.",
            type: 'error',
            color: 'danger'
          });
        }
      } finally {
        if (!cancelled) setHydrating(false);
      }
    })();
    return () => {
      cancelled = true;
      setHydrating(false);
      // if the load never committed, allow a remount to hydrate this chat again
      if (!committed && lastLoadedChatId === intelligenceChatId) lastLoadedChatId = null;
    };
  }, [intelligenceChatId, navigate, setMessages, setHydrating]);
}
